import React, { useState, useEffect, useContext } from "react";
import PosItemCard from "./PosItemCard";
import { Grid } from "@mui/material";
import { SortableContainer, SortableElement, arrayMove } from 'react-sortable-hoc';
import _ from "lodash";
import { useSaveUserSettings } from "../../../shared/hooks/useFrappeDoctype";
import { AppContext } from "../../../AppContext";

// SortableItem component
const SortableItem = SortableElement(({ item, index, onClick, isDraggable }) => (
    <Grid item xs={6}>
        <PosItemCard item={item} index={index} onClick={onClick} isDragDropMode={isDraggable}/>
    </Grid>
));

// SortableList component
const SortableList = SortableContainer(({ items, onClick, isDraggable }) => (
    <Grid 
        container 
        spacing={3}
        style={{ 
            paddingBottom: '40px' // Increased bottom padding for consistency
        }}
    >
        {items.map((item, index) => (
            <SortableItem key={`item-${item.item_code}`} index={index} item={item} onClick={onClick} isDraggable={isDraggable}/>
        ))}
    </Grid>
));

export default function PosItemList({
    onClick,
    menuItems,
    groupedItems,
    subGroups,
    category,
    subCategory,
    onItemsLoaded,
    isDragDropMode,
    searchText,
}) {
    const { userMenuSettings, menuRootGroupId } = useContext(AppContext);
    const [filteredItems, setFilteredItems] = useState([]);
    const saveUserSettings = useSaveUserSettings();

    useEffect(() => {
        let filteredItems = menuItems || [];

        const rootGroupId = menuRootGroupId || "All Item Groups";

        const query = (searchText || "").trim().toLowerCase();

        if (query.length > 0) {
            filteredItems = _.filter(menuItems, (item) => {
                const code = String(item.item_code || "").toLowerCase();
                const name = String(item.item_name || "").toLowerCase();
                const group = String(item.item_group || "").toLowerCase();
                return (
                    code.includes(query) ||
                    name.includes(query) ||
                    group.includes(query)
                );
            });
        } else {
            const categoryId =
                category?.id === "All Item Groups" ? rootGroupId : category?.id;

            if (categoryId && categoryId !== rootGroupId) {
                let categories = [categoryId];
                const childGroups =
                    (subGroups && subGroups.length > 0
                        ? subGroups
                        : _.find(
                              groupedItems,
                              (groups, parentGroup) =>
                                  parentGroup === categoryId
                          )) || [];

                if (childGroups && childGroups.length > 0) {
                    childGroups.forEach((element) => {
                        categories.push(element.id);
                    });
                }

                filteredItems = _.filter(menuItems, (item) =>
                    _.includes(categories, item.item_group)
                );
            }

            if (subCategory) {
                filteredItems = _.filter(filteredItems, {
                    item_group: subCategory.id,
                });
            }
        }

        // Sort the filtered items based on the saved sorting index
        if (
            userMenuSettings &&
            userMenuSettings.item_sort_indices &&
            userMenuSettings.item_sort_indices.length > 0
        ) {
            const { item_sort_indices } = userMenuSettings;
            const itemIndexMap = item_sort_indices.reduce((acc, item) => {
                acc[item.item_code] = item.index;
                return acc;
            }, {});

            filteredItems = filteredItems.sort((a, b) => {
                const indexA = itemIndexMap[a.item_code] || 0;
                const indexB = itemIndexMap[b.item_code] || 0;
                return indexA - indexB;
            });
        }

        setFilteredItems(filteredItems);
    }, [category, subCategory, userMenuSettings, menuItems, searchText, subGroups, menuRootGroupId]);

    useEffect(() => {
        if (menuItems && userMenuSettings) {
            let filteredItems = menuItems;

            if (userMenuSettings && userMenuSettings.item_sort_indices && userMenuSettings.item_sort_indices.length > 0) {
                const { item_sort_indices } = userMenuSettings;
                const itemIndexMap = item_sort_indices.reduce((acc, item) => {
                    acc[item.item_code] = item.index;
                    return acc;
                }, {});

                filteredItems = filteredItems.sort((a, b) => {
                    const indexA = itemIndexMap[a.item_code] || 0;
                    const indexB = itemIndexMap[b.item_code] || 0;
                    return indexA - indexB;
                });
            }

            setFilteredItems(filteredItems);

            if (onItemsLoaded) {
                onItemsLoaded(menuItems);
            }
        } else {
            setFilteredItems(menuItems);

            if (onItemsLoaded) {
                onItemsLoaded(menuItems);
            }
        }
    }, [menuItems, userMenuSettings]);

    const onSortEnd = ({ oldIndex, newIndex }) => {
        const updatedItems = arrayMove(filteredItems, oldIndex, newIndex);
        setFilteredItems(updatedItems);

        // Save the updated order to your state or backend
        if (onItemsLoaded) {
            onItemsLoaded(updatedItems);
        }

        handleSaveFilters(updatedItems);
    };

    const handleSaveFilters = (reorderedItems) => {
        if (userMenuSettings) {
            const _userSettings = { ...userMenuSettings };
            _userSettings.item_sort_indices = reorderedItems.map((item, index) => ({
                item_code: item.item_code,
                index,
            }));
            saveUserSettings.mutate(
                {
                    doctype: "menu_settings",
                    user_settings: _userSettings,
                },
                {
                    onSuccess: () => {
                        console.table("We have saved!");
                    },
                }
            );
        }
    };

    return (
        <div className={"CategoryView__Main"}>
            {isDragDropMode ? (
                <SortableList items={filteredItems} onSortEnd={onSortEnd} onClick={onClick} axis="xy" isDraggable={isDragDropMode} />
            ) : (
                <Grid
                    container
                    spacing={3}
                    style={{ 
                        width: '100%', 
                        boxSizing: 'border-box',
                        minHeight: 'auto', // Let content determine height
                        height: 'auto', // Allow natural growth
                        paddingBottom: '40px' // Increased bottom padding to ensure content height
                    }}
                >
                    {filteredItems &&
                        filteredItems.length > 0 &&
                        filteredItems.map((entry) => (
                            <Grid item xs={6} key={entry.item_code}>
                                <PosItemCard item={entry} onClick={onClick} isDragDropMode={isDragDropMode}/>
                            </Grid>
                        ))}
                </Grid>
            )}
        </div>
    );
}
